// Process service (Phase 3 slice): the active-processing bootstrap poll. Phase 7 extends this with
// the progress stream + start/stop endpoints. Research Home also reads progress fields for the
// in-progress banner (parity with Replit Home).
import { http } from '@/services/http';
import { endpoints } from '@/services/endpoints';

export interface StagePlanEntry {
  key: string;
  label: string;
  status: 'done' | 'active' | 'pending' | string;
}

export interface SubProgress {
  current: number;
  total: number;
  unit: string;
}

export type ProgressTerminalState =
  | null
  | 'completed'
  | 'stopped'
  | 'error'
  | 'depletion_paused'
  | string;

export type ProgressSourceStatus = 'pending' | 'running' | 'done' | 'skipped' | string;

export interface ProgressDetailSource {
  id: string;
  label: string;
  status: ProgressSourceStatus;
  /** 0..1 fraction complete for this source. */
  progress: number;
  companiesFound: number | null;
}

export interface ProgressDetailBatch {
  current: number;
  total: number;
  kind: string;
}

export interface ProgressDetailCounts {
  workingSet: number | null;
  requested: number | null;
  /** Total companies discovered across every Phase-1 source. */
  discovered?: number | null;
  sourcesTotal?: number | null;
  sourcesDone?: number | null;
  apolloFound?: number | null;
  apolloTarget?: number | null;
  recordsBeforeContent?: number | null;
  [key: string]: number | null | undefined;
}

/** Structured live progress snapshot (target / strategic). Prefer camelCase after normalize. */
export interface ProgressDetail {
  terminalState: ProgressTerminalState;
  phase: string;
  stage: string;
  stageName: string;
  activity: string;
  /** 1-based position in the run's granular step list (0 when unknown). */
  stepIndex: number;
  stepTotal: number;
  stepLabel: string;
  sources: ProgressDetailSource[];
  counts: ProgressDetailCounts;
  batch: ProgressDetailBatch | null;
  weights: Record<string, number>;
  subProgress: SubProgress | null;
}

export interface ActiveProcessing {
  active: boolean;
  completed?: boolean;
  processId?: string | null;
  resultId?: string | null;
  overallPercentage?: number;
  currentStage?: string;
  currentStageName?: string;
  currentSubStage?: string;
  activityMessage?: string;
  processType?: string;
  message?: string;
  redirectUrl?: string;
  status?: string;
  startedAt?: number | null;
  lastUpdated?: number | null;
  stagePlan?: StagePlanEntry[];
  subProgress?: SubProgress | null;
  companyCount?: number | null;
  requestedCompanyCount?: number | null;
  depletionPaused?: boolean;
  depletionProvider?: string | null;
  depletionDetail?: string;
  /** Explicit terminal state from structured progress (separate from percentage). */
  terminalState?: ProgressTerminalState;
  /** Per-source Phase-1 fractions (0..1), when exposed top-level. */
  phase1SourceProgress?: Record<string, number>;
  /** Structured detail panel payload (camelCase after normalize). */
  progressDetail?: ProgressDetail | null;
}

export interface ProcessService {
  getActiveProcessing(): Promise<ActiveProcessing>;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function pick<T>(obj: Record<string, unknown>, ...keys: string[]): T | undefined {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined) {
      return obj[key] as T;
    }
  }
  return undefined;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value);
}

function normalizeSubProgress(raw: unknown): SubProgress | null {
  const obj = asRecord(raw);
  if (!obj) return null;
  const current = asNumber(pick(obj, 'current'));
  const total = asNumber(pick(obj, 'total'));
  if (current == null || total == null) return null;
  return {
    current,
    total,
    unit: asString(pick(obj, 'unit') ?? ''),
  };
}

function normalizeStagePlan(raw: unknown): StagePlanEntry[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.map((entry, index) => {
    const obj = asRecord(entry) ?? {};
    return {
      key: asString(pick(obj, 'key') ?? `stage_${index}`),
      label: asString(pick(obj, 'label') ?? pick(obj, 'key') ?? `Stage ${index + 1}`),
      status: asString(pick(obj, 'status') ?? 'pending'),
    };
  });
}

function normalizeSource(raw: unknown, index: number): ProgressDetailSource {
  const obj = asRecord(raw) ?? {};
  const progress = asNumber(pick(obj, 'progress')) ?? 0;
  return {
    id: asString(pick(obj, 'id') ?? `source_${index}`),
    label: asString(pick(obj, 'label') ?? pick(obj, 'id') ?? `Source ${index + 1}`),
    status: asString(pick(obj, 'status') ?? 'pending'),
    progress: Math.max(0, Math.min(1, progress)),
    companiesFound: asNumber(pick(obj, 'companiesFound', 'companies_found')),
  };
}

function normalizeBatch(raw: unknown): ProgressDetailBatch | null {
  const obj = asRecord(raw);
  if (!obj) return null;
  const current = asNumber(pick(obj, 'current'));
  const total = asNumber(pick(obj, 'total'));
  if (current == null || total == null) return null;
  return {
    current,
    total,
    kind: asString(pick(obj, 'kind') ?? ''),
  };
}

function normalizeCounts(raw: unknown): ProgressDetailCounts {
  const obj = asRecord(raw) ?? {};
  const counts: ProgressDetailCounts = {
    workingSet: asNumber(pick(obj, 'workingSet', 'working_set')),
    requested: asNumber(pick(obj, 'requested')),
    discovered: asNumber(pick(obj, 'discovered')),
    sourcesTotal: asNumber(pick(obj, 'sourcesTotal', 'sources_total')),
    sourcesDone: asNumber(pick(obj, 'sourcesDone', 'sources_done')),
    apolloFound: asNumber(pick(obj, 'apolloFound', 'apollo_found')),
    apolloTarget: asNumber(pick(obj, 'apolloTarget', 'apollo_target')),
    recordsBeforeContent: asNumber(pick(obj, 'recordsBeforeContent', 'records_before_content')),
  };
  for (const [key, value] of Object.entries(obj)) {
    if (key in counts) continue;
    const n = asNumber(value);
    if (n != null) counts[key] = n;
  }
  return counts;
}

function normalizeWeights(raw: unknown): Record<string, number> {
  const obj = asRecord(raw);
  if (!obj) return {};
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(obj)) {
    const n = asNumber(value);
    if (n != null) out[key] = n;
  }
  return out;
}

function normalizePhase1SourceProgress(raw: unknown): Record<string, number> | undefined {
  const obj = asRecord(raw);
  if (!obj) return undefined;
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(obj)) {
    const n = asNumber(value);
    if (n != null) out[key] = Math.max(0, Math.min(1, n));
  }
  return out;
}

/** Normalize snake_case or camelCase `progress_detail` / `progressDetail` payloads. */
export function normalizeProgressDetail(raw: unknown): ProgressDetail | null {
  const obj = asRecord(raw);
  if (!obj) return null;
  const sourcesRaw = pick<unknown>(obj, 'sources');
  const sources = Array.isArray(sourcesRaw)
    ? sourcesRaw.map((entry, index) => normalizeSource(entry, index))
    : [];
  return {
    terminalState: (pick(obj, 'terminalState', 'terminal_state') as ProgressTerminalState) ?? null,
    phase: asString(pick(obj, 'phase') ?? ''),
    stage: asString(pick(obj, 'stage') ?? ''),
    stageName: asString(pick(obj, 'stageName', 'stage_name') ?? ''),
    activity: asString(pick(obj, 'activity') ?? ''),
    stepIndex: asNumber(pick(obj, 'stepIndex', 'step_index')) ?? 0,
    stepTotal: asNumber(pick(obj, 'stepTotal', 'step_total')) ?? 0,
    stepLabel: asString(pick(obj, 'stepLabel', 'step_label') ?? ''),
    sources,
    counts: normalizeCounts(pick(obj, 'counts')),
    batch: normalizeBatch(pick(obj, 'batch')),
    weights: normalizeWeights(pick(obj, 'weights')),
    subProgress: normalizeSubProgress(pick(obj, 'subProgress', 'sub_progress')),
  };
}

/** Accept mixed snake_case / camelCase active-processing payloads from poll or SSE. */
export function normalizeActiveProcessing(raw: unknown): ActiveProcessing {
  const obj = asRecord(raw) ?? {};
  const progressDetail = normalizeProgressDetail(pick(obj, 'progressDetail', 'progress_detail'));
  const stagePlan = normalizeStagePlan(pick(obj, 'stagePlan', 'stage_plan'));
  const subProgress = normalizeSubProgress(pick(obj, 'subProgress', 'sub_progress'));
  const phase1SourceProgress = normalizePhase1SourceProgress(
    pick(obj, 'phase1SourceProgress', 'phase1_source_progress'),
  );
  const terminalState =
    (pick(obj, 'terminalState', 'terminal_state') as ProgressTerminalState | undefined) ??
    progressDetail?.terminalState ??
    null;

  return {
    active: Boolean(pick(obj, 'active') ?? false),
    completed: pick<boolean>(obj, 'completed'),
    processId: (pick(obj, 'processId', 'process_id') as string | null | undefined) ?? null,
    resultId: (pick(obj, 'resultId', 'result_id') as string | null | undefined) ?? null,
    overallPercentage: asNumber(pick(obj, 'overallPercentage', 'overall_percentage')) ?? undefined,
    currentStage: pick<string>(obj, 'currentStage', 'current_stage'),
    currentStageName: pick<string>(obj, 'currentStageName', 'current_stage_name'),
    currentSubStage: pick<string>(obj, 'currentSubStage', 'current_sub_stage'),
    activityMessage: pick<string>(obj, 'activityMessage', 'activity_message'),
    processType: pick<string>(obj, 'processType', 'process_type'),
    message: pick<string>(obj, 'message'),
    redirectUrl: pick<string>(obj, 'redirectUrl', 'redirect_url'),
    status: pick<string>(obj, 'status'),
    startedAt: asNumber(pick(obj, 'startedAt', 'started_at')),
    lastUpdated: asNumber(pick(obj, 'lastUpdated', 'last_updated')),
    stagePlan,
    subProgress,
    companyCount: asNumber(pick(obj, 'companyCount', 'company_count')),
    requestedCompanyCount: asNumber(pick(obj, 'requestedCompanyCount', 'requested_company_count')),
    depletionPaused: pick<boolean>(obj, 'depletionPaused', 'depletion_paused'),
    depletionProvider:
      (pick(obj, 'depletionProvider', 'depletion_provider') as string | null | undefined) ?? null,
    depletionDetail: pick<string>(obj, 'depletionDetail', 'depletion_detail'),
    terminalState,
    phase1SourceProgress,
    progressDetail,
  };
}

export const processService: ProcessService = {
  getActiveProcessing: async () => {
    const raw = await http<unknown>(endpoints.profile.activeProcessing);
    return normalizeActiveProcessing(raw);
  },
};
